import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';

import { TankRow } from '../components/dashboard/TankRow';
import { SiteMap } from '../components/dashboard/SiteMap';
import { SearchableSelect } from '../components/layout/SearchableSelect';

import { UnitType, UserRole } from '../types/index';
import './Dashboard.css';

type ViewMode = 'list' | 'map';

export function Dashboard() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const {
    organizations,
    activeOrganization,
    changeActiveOrganization,
    sites,
    units,
    isLoading,
    isSuperAdmin,
    error,
    lastUpdatedAt,
    refetch
  } = useDashboardData();

  // Filters State
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');

  // Filter Units based on search criteria
  const filteredUnits = units.filter(unit => {
    // 1. Dropdown Filters
    if (selectedType && unit.unitType !== selectedType) return false;

    // 2. Site Filter
    if (selectedSiteId && unit.siteId !== selectedSiteId) return false;

    return true;
  });

  // Filter Sites based on dropdown selections
  const filteredSites = sites.filter(site => {
    if (selectedSiteId && site.id !== selectedSiteId) return false;
    
    // In map mode, filter sites that don't match criteria
    if (viewMode === 'map') {
      const siteHasMatches = filteredUnits.some(u => u.siteId === site.id);
      if (!siteHasMatches) return false;
    }

    return true;
  });

  const isOrgEncapsulated = activeOrganization?.role === UserRole.OrgAdmin || activeOrganization?.role === UserRole.SuperAdmin;

  if (isLoading) {
    return (
      <div className="page dashboard-page">
        <div className="page__header dashboard-header-bar">
          <h1 className="page__title">Dashboard</h1>
          <p className="page__subtitle">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Load failed and there's nothing cached to show — render an explicit error
  // state instead of an empty dashboard that looks like "no tanks" (D1).
  if (error && sites.length === 0 && units.length === 0) {
    return (
      <div className="page dashboard-page">
        <div className="page__header dashboard-header-bar">
          <h1 className="page__title">Dashboard</h1>
        </div>
        <div className="dashboard-error-state">
          <p className="dashboard-error-state__title">Unable to load dashboard</p>
          <p className="dashboard-error-state__detail">{error}</p>
          <button className="dashboard-error-retry-btn" onClick={refetch} type="button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page dashboard-page">
      <div className="page__header dashboard-header-bar">
          <h1 className="page__title">Dashboard</h1>
      </div>

      {/* Refresh failed but we still have earlier data on screen — warn without wiping it */}
      {error && (
        <div className="dashboard-error-banner" role="alert">
          <span>
            {error}
            {lastUpdatedAt && ` Showing data as of ${lastUpdatedAt.toLocaleTimeString()}.`}
          </span>
          <button className="dashboard-error-retry-btn" onClick={refetch} type="button">
            Retry
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="dashboard-filters">
        {(isSuperAdmin || organizations.some(o => o.role !== 'SiteUser')) && (
          <div className="filter-group">
            <label className="filter-label" htmlFor="filter-org">Active Organization Context</label>
            <SearchableSelect
              id="filter-org"
              value={activeOrganization?.tenantId || ''}
              onChange={(val) => {
                changeActiveOrganization(val);
                setSelectedSiteId(''); // reset site
              }}
              options={organizations.map(org => ({
                value: org.tenantId,
                label: org.name
              }))}
              placeholder="Select Organization..."
            />
          </div>
        )}
        
        <div className="filter-group">
          <label className="filter-label" htmlFor="filter-site">Site Filter</label>
          <SearchableSelect
            id="filter-site"
            value={selectedSiteId}
            onChange={(val) => setSelectedSiteId(val)}
            options={[
              { value: '', label: 'All Available Sites' },
              ...sites.map(site => ({
                value: site.id,
                label: site.name
              }))
            ]}
            placeholder="Select Site..."
          />
        </div>

        <div className="filter-group">
          <label className="filter-label" htmlFor="filter-type">Unit Type</label>
          <select 
            className="filter-select" 
            id="filter-type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value={UnitType.MobileStation}>Mobile Station</option>
            <option value={UnitType.Repeater}>Repeater</option>
          </select>
        </div>
      </div>

      {/* View Toggle Header */}
      <div className="dashboard-subbar">
        <span className="results-counter">
          Showing {filteredUnits.length} active units across {filteredSites.length} sites
          {lastUpdatedAt && (
            <span className="data-freshness"> · Data as of {lastUpdatedAt.toLocaleTimeString()}</span>
          )}
        </span>

        <div className="dashboard-view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
            type="button"
          >
            List View
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'map' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('map')}
            type="button"
          >
            Map View
          </button>
        </div>
      </div>



      {/* Content Area */}
      <div className="dashboard-content">
        {viewMode === 'list' ? (
          /* List View Execution */
          isOrgEncapsulated && activeOrganization ? (
            /* Organization Encapsulated Box Representation */
            <div className="org-encapsulation-box">
              <div className="org-encapsulation-header">
                <Link to={`/dashboard/organizations`} className="org-title-link hyperlink">
                  Organization: {activeOrganization.name}
                </Link>
              </div>
              <div className="org-encapsulation-body">
                {filteredSites.map(site => {
                  const siteUnits = filteredUnits.filter(u => u.siteId === site.id);
                  return (
                    <div key={site.id} className="site-group">
                      <div className="site-group-header">
                        <Link to={`/dashboard/sites?siteId=${site.id}`} className="site-title-link hyperlink">
                          {site.name}
                        </Link>
                      </div>

                      <div className="site-group-body">
                        {siteUnits.length > 0 ? (
                          siteUnits.map(unit => (
                            <TankRow key={unit.unitId} unit={unit as any} />
                          ))
                        ) : (
                          <p className="no-units-fallback">No active units match the search filters at this site.</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Unencapsulated flat site list */
            filteredSites.map(site => {
              const siteUnits = filteredUnits.filter(u => u.siteId === site.id);
              return (
                <div key={site.id} className="site-group unencapsulated">
                  <div className="site-group-header">
                    <Link to={`/dashboard/sites?siteId=${site.id}`} className="site-title-link hyperlink">
                      {site.name}
                    </Link>
                  </div>

                  <div className="site-group-body">
                    {siteUnits.length > 0 ? (
                      siteUnits.map(unit => (
                        <TankRow key={unit.unitId} unit={unit as any} />
                      ))
                    ) : (
                      <p className="no-units-fallback">No active units match the search filters at this site.</p>
                    )}
                  </div>
                </div>
              );
            })
          )
        ) : (
          /* Map View Execution */
          <SiteMap units={filteredUnits as any} sites={filteredSites} activeSiteId={selectedSiteId} />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
